import { Blac, Cubit } from "blac-next";
import { tracker } from "src/state/state";
import {
  findCareTeamRole,
  type CareTeamRole
} from "src/ui/components/CareTeamSection/careTeamRoles";

interface CareTeamDetailState {
  /**
   * The `id` of the currently open Care Team role from
   * `careTeamRoles.ts`, or `null` when the detail sheet is closed.
   */
  activeRoleId: string | null;
  /**
   * The last opened role, preserved during the sheet's exit animation so
   * content stays stable while the sheet slides away. Only cleared when a
   * different role opens.
   */
  displayedRole: CareTeamRole | null;
}

/**
 * Owns which Care Team role's detail sheet is currently open.
 *
 * Kept as a singleton (`keepAlive`) so any component can request the sheet
 * open without having to hoist state — mirrors the `HashActionBloc`
 * pattern used by `HashBottomSheet`.
 */
export default class CareTeamDetailCubit extends Cubit<CareTeamDetailState> {
  static keepAlive = true;

  constructor() {
    super({ activeRoleId: null, displayedRole: null });
  }

  open = (roleId: string): void => {
    const nextRole = findCareTeamRole(roleId);

    this.patch({
      activeRoleId: roleId,
      displayedRole: nextRole ?? this.state.displayedRole
    });

    tracker.track("Care Team Detail Opened", {
      data: { roleId }
    });
  };

  close = (): void => {
    const { activeRoleId } = this.state;

    if (activeRoleId) {
      tracker.track("Care Team Detail Closed", {
        data: { roleId: activeRoleId }
      });
    }

    this.patch({ activeRoleId: null });
  };
}

export const careTeamDetailController = Blac.getBloc(CareTeamDetailCubit);
